import {
  ICheckInOutReport,
  ISubmitCheckInOutReportFields,
  CheckInOutReportType,
  AcknowledgementOutcome,
} from '../interfaces/heavyDutyMachinery/ICheckInOut';
import { apiService, apiUploadService } from './ApiService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export const getCheckInOutReports = async (bookingId: string): Promise<ICheckInOutReport[]> => {
  const response = await apiService<ICheckInOutReport[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CHECK_IN_OUT(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load check-in/check-out reports');
};

// The one-shot, atomic submit -- CheckInOutController.SubmitReport creates
// the report AND its photos in the same call, and SubmittedAt is set
// immediately (see CheckInOutReport's own doc comment on the backend): a
// retry after a failed attempt re-sends this exact same request, no partial
// "resume" endpoint exists to resume from. A caller whose retry lands after
// the FIRST attempt actually succeeded server-side (the response just never
// made it back, e.g. the connection dropped) gets a 409 here -- see
// ALREADY_SUBMITTED_MARKER below for how callers should treat that as a
// success, not a failure to surface.
export const ALREADY_SUBMITTED_MARKER = 'already exists for this booking';

/**
 * Takes the money that was frozen, on a charge-at-handover booking.
 *
 * Called BEFORE the walkaround starts, so a host finds out about a declined
 * card with the machine still on the lorry rather than after six photos and a
 * signature. Submitting the delivery note runs the very same capture as a
 * guard, so skipping this changes the outcome not at all -- only when the bad
 * news arrives.
 *
 * Succeeds silently for a booking that was never this kind, and for one
 * already captured, so it can be called without checking first.
 */
export const captureBookingAtHandover = async (bookingId: string): Promise<void> => {
  const response = await apiService<{ captured: boolean }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CHECK_IN_OUT_CAPTURE(bookingId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'CaptureFailed');
};

export const submitCheckInOutReport = async (
  bookingId: string,
  reportType: CheckInOutReportType,
  fields: ISubmitCheckInOutReportFields,
  photos: File[]
): Promise<ICheckInOutReport> => {
  const formData = new FormData();
  if (fields.fuelPercent != null) formData.append('fuelPercent', String(fields.fuelPercent));
  if (fields.engineHours != null) formData.append('engineHours', String(fields.engineHours));
  if (fields.damageNotes) formData.append('damageNotes', fields.damageNotes);
  if (fields.damageClaimAmount != null) formData.append('damageClaimAmount', String(fields.damageClaimAmount));
  formData.append('latitude', String(fields.latitude));
  formData.append('longitude', String(fields.longitude));
  photos.forEach((file) => formData.append('photos', file));

  const response = await apiUploadService<ICheckInOutReport>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CHECK_IN_OUT_SUBMIT(bookingId, reportType),
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to submit report');
};

/** Fetches one evidence photo as a local, revokable object URL -- CheckInOutPhoto is stored behind an authenticated download endpoint (never a bare URL), same reasoning InvoiceService.downloadInvoice's own fetch-then-blob already documents. Caller owns revoking it (URL.revokeObjectURL) once done. */
export const getCheckInOutPhotoObjectUrl = async (
  bookingId: string,
  reportType: CheckInOutReportType,
  photoId: string
): Promise<string> => {
  const accessToken = getLocalStorageKey('accessToken');
  const url = API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CHECK_IN_OUT_PHOTO_DOWNLOAD(bookingId, reportType, photoId);
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });
  if (!response.ok) throw new Error(`Failed to download photo (HTTP ${response.status})`);
  const blob = await response.blob();
  return window.URL.createObjectURL(blob);
};

/**
 * The counterparty's half of the albarán. `remarks` is required by the
 * server for anything other than a plain 'Accepted' -- an outcome that
 * isn't a straight yes is worthless on the record without the reason.
 * `signature` is optional: it is what people expect a delivery note to
 * carry, but the binding record is the authenticated acknowledgement
 * itself, not the picture of a squiggle.
 */
export const acknowledgeCheckInOutReport = async (
  bookingId: string,
  reportType: CheckInOutReportType,
  outcome: AcknowledgementOutcome,
  remarks?: string,
  signature?: Blob
): Promise<ICheckInOutReport> => {
  const formData = new FormData();
  formData.append('outcome', outcome);
  if (remarks) formData.append('remarks', remarks);
  if (signature) formData.append('signature', signature, 'signature.png');

  const response = await apiUploadService<ICheckInOutReport>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_CHECK_IN_OUT_ACKNOWLEDGE(bookingId, reportType),
    formData,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to acknowledge report');
};
